import { useState } from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAppliances } from "@/hooks/useAppliances";
import { usePaintCodes } from "@/hooks/usePaintCodes";
import { useHomeLibrary } from "@/hooks/useHomeLibrary";
import { usePropertyTransfer } from "@/hooks/usePropertyTransfer";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { Loader2, Copy, Check, Refrigerator, Paintbrush, FileText } from "lucide-react";
import GlassCard from "./GlassCard";
import { toast } from "@/hooks/use-toast";

const PropertyTransferSheet = ({ open, onOpenChange }) => {
    const { selectedProperty: activeProperty } = usePropertyContext();
    const { appliances } = useAppliances(activeProperty?.id);
    const { paintCodes } = usePaintCodes(activeProperty?.id);
    const { libraryItems: vaultItems } = useHomeLibrary(activeProperty?.id);
    const { createTransfer, loading } = usePropertyTransfer();

    const [step, setStep] = useState("select");
    const [selectedAppliances, setSelectedAppliances] = useState([]);
    const [selectedPaintCodes, setSelectedPaintCodes] = useState([]);
    const [selectedHomeLibrary, setSelectedHomeLibrary] = useState([]);
    const [claimUrl, setClaimUrl] = useState("");
    const [copied, setCopied] = useState(false);

    const handleToggleAppliance = (id) => {
        setSelectedAppliances((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleTogglePaintCode = (id) => {
        setSelectedPaintCodes((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleToggleHomeLibrary = (id) => {
        setSelectedHomeLibrary((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        setSelectedAppliances(appliances.map((a) => a.id));
        setSelectedPaintCodes(paintCodes.map((p) => p.id));
        setSelectedHomeLibrary(vaultItems.map((v) => v.id));
    };

    const handleProceed = async () => {
        if (selectedAppliances.length === 0 && selectedPaintCodes.length === 0 && selectedHomeLibrary.length === 0) {
            toast({
                title: "Select Items",
                description: "Please select at least one item to transfer.",
                variant: "destructive",
            });
            return;
        }

        if (!activeProperty) return;

        const url = await createTransfer({
            propertyId: activeProperty.id,
            recipientEmail: "", // No longer required in UI
            selectedAppliances,
            selectedPaintCodes,
            selectedHomeLibrary,
        });

        if (url) {
            setClaimUrl(url);
            setStep("done");
        }
    };

    const handleCopyLink = async () => {
        await navigator.clipboard.writeText(claimUrl);
        setCopied(true);
        toast({
            title: "Link Copied",
            description: "Link copied! REMINDER: Once the transfer is claimed, please delete this property from your profile and remind other members to do the same.",
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const handleClose = (open) => {
        if (!open) {
            setStep("select");
            setSelectedAppliances([]);
            setSelectedPaintCodes([]);
            setSelectedHomeLibrary([]);
            setClaimUrl("");
        }
        onOpenChange(open);
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent side="bottom" onOpenAutoFocus={(e) => e.preventDefault()} className="max-h-[85vh] rounded-t-3xl flex flex-col">
                <SheetHeader className="pb-4 flex-shrink-0">
                    <SheetTitle className="text-xl font-bold text-foreground">
                        {step === "select" && "Transfer Property"}
                        {step === "done" && "Transfer Ready!"}
                    </SheetTitle>
                    <SheetDescription>
                        {step === "select" && "Select items that stay with the property"}
                        {step === "done" && "Share this link with the recipient"}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4 pb-4">
                    {step === "select" && (
                        <>
                            {/* Select All */}
                            <Button variant="outline" size="sm" onClick={handleSelectAll}>
                                Select All
                            </Button>

                            {/* Appliances */}
                            {appliances.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Refrigerator className="w-4 h-4 text-primary" />
                                        <h3 className="font-medium text-sm text-foreground">Appliances</h3>
                                    </div>
                                    {appliances.map((appliance) => (
                                        <GlassCard
                                            key={appliance.id}
                                            className="p-3 flex items-center gap-3"
                                        >
                                            <Checkbox
                                                id={`appliance-${appliance.id}`}
                                                checked={selectedAppliances.includes(appliance.id)}
                                                onCheckedChange={() => handleToggleAppliance(appliance.id)}
                                            />
                                            <label
                                                htmlFor={`appliance-${appliance.id}`}
                                                className="flex-1 cursor-pointer"
                                            >
                                                <span className="font-medium text-foreground">
                                                    {appliance.brand} {appliance.name}
                                                </span>
                                                <p className="text-xs text-muted-foreground">
                                                    {appliance.category || "Appliance"}
                                                </p>
                                            </label>
                                        </GlassCard>
                                    ))}
                                </div>
                            )}

                            {/* Paint Codes */}
                            {paintCodes.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Paintbrush className="w-4 h-4 text-primary" />
                                        <h3 className="font-medium text-sm text-foreground">Paint Codes</h3>
                                    </div>
                                    {paintCodes.map((paint) => (
                                        <GlassCard
                                            key={paint.id}
                                            className="p-3 flex items-center gap-3"
                                        >
                                            <Checkbox
                                                id={`paint-${paint.id}`}
                                                checked={selectedPaintCodes.includes(paint.id)}
                                                onCheckedChange={() => handleTogglePaintCode(paint.id)}
                                            />
                                            <label
                                                htmlFor={`paint-${paint.id}`}
                                                className="flex-1 cursor-pointer"
                                            >
                                                <span className="font-medium text-foreground">{paint.room}</span>
                                                <p className="text-xs text-muted-foreground">
                                                    {paint.brand} {paint.code}
                                                </p>
                                            </label>
                                        </GlassCard>
                                    ))}
                                </div>
                            )}

                            {/* Home Library Documents */}
                            {vaultItems.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FileText className="w-4 h-4 text-primary" />
                                        <h3 className="font-medium text-sm text-foreground">Documents (Plans, COCs, etc.)</h3>
                                    </div>
                                    {vaultItems.map((item) => (
                                        <GlassCard
                                            key={item.id}
                                            className="p-3 flex items-center gap-3"
                                        >
                                            <Checkbox
                                                id={`vault-${item.id}`}
                                                checked={selectedHomeLibrary.includes(item.id)}
                                                onCheckedChange={() => handleToggleHomeLibrary(item.id)}
                                            />
                                            <label
                                                htmlFor={`vault-${item.id}`}
                                                className="flex-1 cursor-pointer"
                                            >
                                                <span className="font-medium text-foreground">{item.label}</span>
                                                <p className="text-xs text-muted-foreground capitalize">
                                                    {item.category}
                                                </p>
                                            </label>
                                        </GlassCard>
                                    ))}
                                </div>
                            )}

                            {appliances.length === 0 && paintCodes.length === 0 && vaultItems.length === 0 && (
                                <GlassCard className="p-6 text-center">
                                    <p className="text-muted-foreground">
                                        No items to transfer. Add appliances, paint codes, or documents first.
                                    </p>
                                </GlassCard>
                            )}
                        </>
                    )}



                    {step === "done" && (
                        <div className="space-y-4">
                            <GlassCard className="p-4">
                                <Label className="text-sm text-muted-foreground">Transfer Link</Label>
                                <div className="flex gap-2 mt-2">
                                    <Input
                                        value={claimUrl}
                                        readOnly
                                        className="rounded-xl text-xs"
                                    />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={handleCopyLink}
                                        className="shrink-0"
                                    >
                                        {copied ? (
                                            <Check className="w-4 h-4 text-emerald-500" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </Button>
                                </div>
                            </GlassCard>

                            <GlassCard className="p-4 bg-amber-50/50 border-amber-200">
                                <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-2">
                                    <span className="p-1 bg-amber-100 rounded-full">⚠️</span>
                                    Important Next Steps
                                </h4>
                                <p className="text-xs text-amber-800 leading-relaxed">
                                    This transfer creates a copy of your data for the new owner. Once they have successfully claimed it, <strong>please delete this house from your profile</strong> (and ask other members to do the same) to permanently remove it from your account.
                                </p>
                            </GlassCard>

                            <p className="text-[11px] text-muted-foreground text-center px-4">
                                The recipient has 7 days to claim this transfer. They will need to sign in or create a HomeEzi account to accept.
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex-shrink-0 pt-2">
                    {step === "select" && (
                        <Button
                            onClick={handleProceed}
                            className="w-full rounded-xl py-5"
                            disabled={loading || (selectedAppliances.length === 0 && selectedPaintCodes.length === 0 && selectedHomeLibrary.length === 0)}
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                "Generate Transfer Link"
                            )}
                        </Button>
                    )}

                    {step === "done" && (
                        <Button
                            onClick={() => handleClose(false)}
                            className="w-full rounded-xl py-5"
                        >
                            Done
                        </Button>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default PropertyTransferSheet;
